import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SubletVendorResponse, Task, VendorRegistrationData, VendorSearchParams, VendorSearchResponse, VendorTask, ExternalVendorTaskResponse } from '@org/interfaces';
import { environment } from '@org/shared-services';
import { VendorUser } from '@org/utilities';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { TaskService } from '@org/shared-services';

@Injectable({
  providedIn: 'root'
})
export class VendorsService {
  private apiUrl = `${environment.apiUrl}/api/vendor`;
  private vendorsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public vendors$: Observable<any> = this.vendorsSubject.asObservable();

  constructor(private http: HttpClient, private taskService: TaskService) { }

  createVendor(vendor: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}`, vendor);
  }

  createTempVendor(vendor: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tempUser`, vendor);
  }

  getVendorById(vendor: any): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${vendor._id}`);
  }

  updateVendor(formData: FormData, vendor: VendorTask): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${vendor._id}`, formData);
  }

  updateVendorTenant(vendor: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/tenant/${vendor._id}`, vendor);
  }

  updateVendorTask(vendor: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/tasks/${vendor._id}`, vendor);
  }

  deleteVendor(vendor: any): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${vendor._id}`);
  }

  imageUpload(formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/media`, formData);
  }

  getVendorTasks(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/activeWork`);
  }

  getVendorTasksByVendorId(): Observable<ExternalVendorTaskResponse> {
    return this.http.get<ExternalVendorTaskResponse>(`${this.apiUrl}/external-vendor`);
  }

  updateTaskStatus(taskId: string, status: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${taskId}/status`, { status }).pipe(
      tap((updatedVendorTask: any) => {
        // Update the main task status through task service
        if (updatedVendorTask.task) {
          const updatedTask = {
            ...updatedVendorTask.task,
            taskCompleted: status === 'completed'
          };
          this.taskService.updateTask(updatedTask._id, updatedTask, 'Vendor task status updated', 'update').subscribe();
        }
      })
    );
  }

  getNewTasksCount(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/new-count`);
  }

  registerVendor(registrationData: VendorRegistrationData, token: string): Observable<any> {
    const payload = {
      ...registrationData,
      registrationToken: token
    };
    return this.http.post(`${this.apiUrl}/vendor-registration`, payload);
  }
   // Add methods to control invite panel visibility
   openInviteVendorPanel() {
    this.vendorsSubject.next(true);
  }

  closeInviteVendorPanel() {
    this.vendorsSubject.next(false);
  }

  toggleInviteVendorPanel() {
    this.vendorsSubject.next(!this.vendorsSubject.getValue());
  }

  // Vendor Approval
  getPendingVendors(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/pending-vendors`);
  }

  approveVendor(vendorId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/vendors/${vendorId}/approve`, {});
  }

  rejectVendor(vendorId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/vendors/${vendorId}/reject`, {});
  }

  deleteImage(vendorId: string, mediaId: string): Observable<VendorTask> {
    return this.http.delete<VendorTask>(`${this.apiUrl}/${vendorId}/media/${mediaId}`);
  }

  getVendorOwnTasks(): Observable<VendorTask[]> {
    return this.http.get<VendorTask[]>(`${this.apiUrl}/vendor-tasks`);
  }

  getTasksWithSublets(): Observable<Task[]> {
    return this.http.get<Task[]>(`${this.apiUrl}/tasks/sublets`);
  }

  getSubletVendorAssignments(taskId: string): Observable<SubletVendorResponse> {
    return this.http.get<SubletVendorResponse>(`${this.apiUrl}/vendor/${taskId}/sublet-vendors`);
  }

  markVendorTaskAsRead(vendorId: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/vendor/${vendorId}/read`, {});
  }

  /**
   * Search vendors with optional filtering
   * @param params Search parameters including query, service, and limit
   * @returns Observable of grouped vendor search results
   */
  searchVendors(params: VendorSearchParams): Observable<VendorSearchResponse> {
    let httpParams = new HttpParams();

    // Add parameters if they exist
    if (params.query) {
      httpParams = httpParams.set('query', params.query);
    }
    if (params.service) {
      httpParams = httpParams.set('service', params.service);
    }
    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }

    return this.http.get<VendorSearchResponse>(`${this.apiUrl}/search/vendors`, {
      params: httpParams
    });
  }

  /**
   * Mark a vendor task as completed
   * @param vendorId The ID of the vendor task to complete
   * @returns Observable<any>
   */
  completeVendorTask(vendorId: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${vendorId}/complete`, { completed: true });
  }

  /**
   * Mark a vendor task as completed by an admin
   * @param vendorId The ID of the vendor task to complete
   * @returns Observable<any>
   */
  completeVendorTaskByAdmin(vendorId: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${vendorId}/complete-by-admin`, { completed: true });
  }
}
